/**
 * Content hashes for large arrays, cached by identity (the per-tick checksum's building block,
 * src/match/tickChecksum.ts). The sim replaces big arrays when they change — craters, fog updates
 * copy — so a hash per array object stays right. The one exception writes in place for speed
 * (core/fog.ts revealAtPoint, the probe and drop-can scans) and calls `contentChanged` on the arrays it
 * touched; anything else that ever edits a state array in place must do the same.
 */

type Words = Uint32Array | Uint8Array;
const typed = new WeakMap<ArrayBufferView, { full: number; words: Words }>();
const plain = new WeakMap<unknown[], number>();

export const mix = (h: number, v: number): number => Math.imul(h ^ (v | 0), 16777619);

function wordsOf(a: ArrayBufferView): Words {
  return a.byteOffset % 4 === 0 && a.byteLength % 4 === 0
    ? new Uint32Array(a.buffer, a.byteOffset, a.byteLength / 4)
    : new Uint8Array(a.buffer, a.byteOffset, a.byteLength);
}

/** A typed array's full content hash (cached until `contentChanged`). */
export function typedArrayHash(a: ArrayBufferView): { full: number; words: Words } {
  let c = typed.get(a);
  if (c === undefined) {
    const words = wordsOf(a);
    let full = 0x811c9dc5;
    for (let i = 0; i < words.length; i++) full = mix(full, words[i]);
    c = { full, words };
    typed.set(a, c);
  }
  return c;
}

/** A large plain array's content hash, computed by `hashOf` once (cached until `contentChanged`). */
export function plainArrayHash(a: unknown[], hashOf: (a: unknown[]) => number): number {
  let h = plain.get(a);
  if (h === undefined) { h = hashOf(a); plain.set(a, h); }
  return h;
}

/** An array was edited in place: forget its cached hash. */
export function contentChanged(...arrays: object[]): void {
  for (const a of arrays) {
    if (ArrayBuffer.isView(a)) typed.delete(a);
    else if (Array.isArray(a)) plain.delete(a);
  }
}
